/* @flow */

import * as React from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import querystring from 'qs'

import TruncatedString from '../../../../infrastructure/bindings/TruncatedString'
import FormatCurrency from '../../../../infrastructure/components/FormatCurrency'
import FormatNumber from '../../../../infrastructure/components/FormatNumber'
import DeliveryDate from '../../../../infrastructure/bindings/delivery-date'
import AddToCart from '../AddToCartV2'
import AddToFavorites from '../AddToFavorites'
import InnerDomOverlayTrigger from '../../../../infrastructure/components/InnerDomOverlayTrigger'
import WebshopProductImage from '../WebshopProductImage'
import {
  ColorDots as ColorDotsComponent,
  useCalculatePreviewImagesAndColorDots,
} from '../../../../infrastructure/components/ProductGrid'

import type { ConnectedBrand, Product, WebshopSession } from '../../../types'

const LABEL_MARKUP = 'Markup'
const LABEL_DELIVERY = 'Delivery'
const LABEL_ITEM_NUMBER = 'Item Number'
const LABEL_RRP = 'Rec. Retail Price'
const LABEL_BRAND = 'Brand'

const SHOW_MAX_COLORS = 20
const COLORS_TWO_LINES_THRESHOLD = 8

const SHOW_MAX_PREVIEW_IMAGES = 14
const PREVIEW_IMAGES_TWO_COLUMNS_THRESHOLD = 8

type Props = {
  brand: ConnectedBrand,
  product: Product,
  cartIsSaving: boolean,
  cartRows: Array<CartRow>,
  currency: string,
  cart: Object,
  onCartUpdate: Function,
  showLabels: Array<string>,
  colorAttributeName: ?string,
  colorAttributeColors: { [string]: string },
  perRow?: number,
  product: Product,
  margin?: number,
  webshopSession: WebshopSession,
}

const ProductItem = ({
  brand,
  cartIsSaving,
  cartRows,
  className,
  colorAttributeName,
  colorAttributeColors,
  onCartUpdate,
  navigationLink,
  perRow,
  product,
  refreshCartRows,
  margin,
  query = {},
  showLabels,
  webshopSession,
  singleVariantId,
}: Props) => {
  const [mainImage, setMainImage] = React.useState(product.image)
  const currency = webshopSession.currency
  const { price } = product

  const [previewImages, imagesColor, colorDots] =
    useCalculatePreviewImagesAndColorDots(
      product,
      colorAttributeName,
      colorAttributeColors,
      SHOW_MAX_PREVIEW_IMAGES
    )

  const productLink = React.useMemo(() => {
    let link
    if (navigationLink) {
      link = `/shop/${brand.id}/search/${navigationLink.id}/show/${product.id}`
    } else {
      link = `/shop/${brand.id}/show/${product.id}`
    }

    // since we will search whenever the filter queries update (and we do not want that)
    // we pass the entire search query along to the product page as well
    const newQuery = { ...query }
    if (
      brand.settings.webshop_settings
        .only_show_splitted_attributes_on_product_page &&
      product.split_key &&
      product.split_key.length > 0
    ) {
      newQuery.split_key = product.split_key
    }
    if (singleVariantId) {
      newQuery.variant_id = singleVariantId
    }

    if (Object.keys(newQuery).length > 0) {
      link += `?${querystring.stringify(newQuery)}`
    }

    return link
  }, [brand, product, navigationLink, singleVariantId, query])

  const discountPercentage = React.useMemo(() => {
    if (!price || !price.offer_price || !price.rec_sales_price) {
      return null
    }

    return Math.round(
      ((price.sales_price - price.offer_price) / price.sales_price) * 100
    )
  })

  return (
    <ProductContainer
      className={className}
      margin={margin}
      onMouseLeave={() => setMainImage(product.image)}
      previewImagesCount={previewImages.length}
      perRow={perRow}
    >
      <ProductHoverContainer
        className="product-item-hover-container"
        previewImagesCount={previewImages.length}
      >
        {previewImages.length > 1 && (
          <PreviewImagesContainer>
            {previewImages.map((image, index) => (
              <PreviewImage
                key={index}
                onMouseEnter={() => setMainImage(image)}
              >
                {imagesColor[image] && (
                  <InnerDomOverlayTrigger
                    placement="left"
                    overlay={
                      <ColorTooltip id="webshop-search-product-preview-image-tooltip">
                        {imagesColor[image]}
                      </ColorTooltip>
                    }
                  >
                    <WebshopProductImage
                      id={image}
                      presets="product_search_preview_thumbnail"
                      fallback="product_search_preview_thumbnail"
                    />
                  </InnerDomOverlayTrigger>
                )}
                {!imagesColor[image] && (
                  <WebshopProductImage
                    id={image}
                    presets="product_search_preview_thumbnail"
                    fallback="product_search_preview_thumbnail"
                  />
                )}
              </PreviewImage>
            ))}
          </PreviewImagesContainer>
        )}
      </ProductHoverContainer>

      <ProductInfoImage className="text-center cursor-pointer" to={productLink}>
        <WebshopProductImage
          id={mainImage}
          presets="product_search"
          fallback="product_search"
        />

        {product.webshop_product_label_label !== null && (
          <div
            className="webshop-search-product-label"
            style={{ backgroundColor: product.webshop_product_label_color }}
          >
            {product.webshop_product_label_label}
          </div>
        )}

        {discountPercentage > 0 && (
          <div className="webshop-search-product-offer-circle">
            {discountPercentage}%
          </div>
        )}
      </ProductInfoImage>
      <div className="webshop-search-product-info-container">
        <p className="webshop-search-product-info-title">
          <TruncatedString
            value={`${product.name}${
              product.split_key ? ` ${product.split_key}` : ''
            }`}
            showTooltip={false}
            maxLength="55"
          />
        </p>
        {showLabels.indexOf(LABEL_ITEM_NUMBER) !== -1 && (
          <p className="webshop-search-product-info-item-number">
            <span className="product-info-property" /> {product.item_number}
          </p>
        )}
        <p className="webshop-search-product-info-price">
          <span className="product-info-property" />
          {!price && <span>No price was found in {currency}</span>}
          {price && (
            <span>
              {price.offer_price !== null && (
                <span className="color-alert">
                  <FormatCurrency currency={currency}>
                    {parseFloat(price.offer_price)}
                  </FormatCurrency>
                </span>
              )}
              {price.offer_price === null && (
                <span>
                  <FormatCurrency currency={currency}>
                    {parseFloat(price.price)}
                  </FormatCurrency>
                </span>
              )}
              {price.rec_sales_price !== null && (
                <span>
                  {showLabels.indexOf(LABEL_RRP) !== -1 && (
                    <span>
                      {' / '}
                      <FormatCurrency currency={currency}>
                        {parseFloat(price.rec_sales_price)}
                      </FormatCurrency>
                    </span>
                  )}
                </span>
              )}
            </span>
          )}
        </p>
        <div className="webshop-search-product-info-markup">
          {showLabels.indexOf(LABEL_MARKUP) !== -1 && price && (
            <div>
              <span className="product-info-property" />
              <span className="glyphicon glyphicon-share-alt" />
              {price.offer_price !== null && (
                <FormatNumber
                  value={price.rec_sales_price / price.offer_price}
                />
              )}
              {price.offer_price === null && (
                <FormatNumber
                  value={price.rec_sales_price / price.sales_price}
                />
              )}
            </div>
          )}
        </div>
        {showLabels.indexOf(LABEL_DELIVERY) !== -1 &&
          product.delivery !== null &&
          product.delivery !== undefined && (
            <div className="webshop-search-product-info-delivery">
              <span className="glyphicon glyphicon-time" />
              <DeliveryDate delivery={product.delivery} immediately={true} />
            </div>
          )}
        {showLabels.includes(LABEL_BRAND) && product.subbrand && (
          <ShowLabelBrand>{product.subbrand.name}</ShowLabelBrand>
        )}
        <ColorDotsComponent
          colorDots={colorDots}
          showMaxColors={SHOW_MAX_COLORS}
          twoLinesThreshold={COLORS_TWO_LINES_THRESHOLD}
        />
      </div>

      <div className="webshop-search-product-actions">
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="webshop-search-product-actions-view-tooltip">
              View the product
            </Tooltip>
          }
        >
          <GoToProductLink
            className="webshop-search-product-actions-view"
            to={productLink}
          >
            <span className="lnr lnr-eye" />
          </GoToProductLink>
        </OverlayTrigger>
        <AddToCart
          brand={brand}
          cartIsSaving={cartIsSaving}
          cartRows={cartRows}
          product={product}
          onCartUpdate={onCartUpdate}
          refreshCartRows={refreshCartRows}
          webshopSession={webshopSession}
        />
        <AddToFavorites brand={brand} product={product} />
      </div>
    </ProductContainer>
  )
}

export default ProductItem

const ProductContainer = styled.div`
  flex-basis: ${({
    perRow = 4,
    margin = 6,
  }: {
    perRow?: number,
    margin?: number,
  }) => `calc(${100 / perRow}% - ${margin * 2}px)`};
  position: relative;
  border: 0.5px solid #eee;
  margin: ${({ margin = 6 }: { margin?: number }) => `${margin}px`};
  width: ${({ perRow = 4, margin = 6 }: { perRow?: number, margin?: number }) =>
    `calc(${100 / perRow}% - ${margin * 2}px)`};

  &:hover .product-item-hover-container {
    display: block;
  }

  ${props =>
    props.previewImagesCount > 1 &&
    css`
      &:hover .webshop-search-product-actions {
        left: -1px;
        border-left: 1px solid #eee;
      }
    `}
`

const ProductHoverContainer = styled.div`
  position: absolute;
  left: -1px;
  top: -1px;
  bottom: -1px;
  right: -1px;
  border: 1px solid #cccccc;
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.11);
  background-color: white;

  ${props =>
    props.previewImagesCount > 1 &&
    css`
      left: -66px;
    `}

  ${props =>
    props.previewImagesCount >= PREVIEW_IMAGES_TWO_COLUMNS_THRESHOLD &&
    css`
      left: -121px;
    `}

  display: none;
`

const ProductInfoImage = styled(Link)`
  display: block;
  position: relative;

  > img {
    width: 100%;
  }
`

const PreviewImagesContainer = styled.div`
  display: flex;
  flex-flow: column wrap;
  width: 120px;
  height: ${(PREVIEW_IMAGES_TWO_COLUMNS_THRESHOLD - 1) * 55 + 10}px;
  padding: 5px;
`

const PreviewImage = styled.div`
  width: 45px;
  height: 45px;
  margin: 5px;
`

const ColorTooltip = styled(Tooltip)`
  white-space: nowrap;
`

const ShowLabelBrand = styled.div`
  margin: 10px 0;
`

const GoToProductLink = styled(Link)`
  color: inherit;
`
